/**
 * Shared trigger chrome for `ConnectionSelector` and `DatabaseSelector`, so
 * the two pickers cannot drift apart.
 */

import { useCallback, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui/tooltip';

/**
 * Trigger classes for both selectors. Hover and the open list fill the
 * trigger and brighten its text (the old `hover:opacity-80` dimmed it, which
 * read as disabled); keyboard focus draws an inset ring, because the shadcn
 * base's `focus:outline-none` otherwise leaves nothing visible.
 *
 * The open state keys off `aria-expanded`, not `data-state`: inside a
 * `TooltipTrigger asChild` the tooltip's own `data-state` overwrites the
 * select's.
 */
export const SELECTOR_TRIGGER_CLASS = `
  h-auto w-auto min-w-0 px-1.5 py-0 gap-1 rounded-sm border-0 shadow-none bg-transparent
  inline-flex items-center flex-shrink-0 cursor-pointer text-xs
  focus:ring-0 focus:ring-offset-0
  focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring
  hover:bg-foreground/10 hover:text-foreground
  aria-expanded:bg-foreground/10 aria-expanded:text-foreground
  transition-colors [&>svg:last-child]:hidden
`;

/**
 * Radix Select hands focus back to its trigger when the list closes, and a
 * Radix Tooltip opens on focus — so without this grace window every pick
 * would pop the tooltip. The focus return lands a macrotask after the close
 * commit; 400ms leaves room for a slow frame.
 */
const FOCUS_RETURN_GRACE_MS = 400;

type SelectorTriggerTooltipProps = {
  /** Tooltip body. When absent the trigger renders without a tooltip. */
  content?: ReactNode;
  /** Whether the select's list is open; the tooltip stays shut meanwhile. */
  selectOpen: boolean;
  children: ReactElement;
};

/**
 * Radix Tooltip around a Select trigger that stays shut while the list is
 * open and ignores the focus-return open request when the list closes.
 */
export function SelectorTriggerTooltip({ content, selectOpen, children }: SelectorTriggerTooltipProps) {
  const [open, setOpen] = useState(false);
  const wasSelectOpenRef = useRef(selectOpen);
  const selectClosedAtRef = useRef(0);

  // Layout effect: runs in the commit that closes the list, before Radix's
  // focus return (scheduled from FocusScope's unmount) reaches the trigger.
  // Opening the list from the keyboard never sends the tooltip a close, so
  // it is closed here too — otherwise it would reappear once the list shuts.
  useLayoutEffect(() => {
    if (selectOpen) setOpen(false);
    if (wasSelectOpenRef.current && !selectOpen) selectClosedAtRef.current = Date.now();
    wasSelectOpenRef.current = selectOpen;
  }, [selectOpen]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next && (selectOpen || Date.now() - selectClosedAtRef.current < FOCUS_RETURN_GRACE_MS)) return;
      setOpen(next);
    },
    [selectOpen],
  );

  if (!content) return children;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={open && !selectOpen} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" align="start" data-testid="selector-trigger-tooltip">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
